import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Clock, AlertTriangle, TrendingUp, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import KPICard from '../components/KPICard';
import AIRecommendation from '../components/AIRecommendation';
import { apiFetch } from '../lib/api';

const Overview = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  
  useEffect(() => {
    fetchProjects();
    fetchInsights();
  }, []);
  
  const fetchProjects = async () => {
    try {
      const response = await apiFetch('/api/projects');
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await apiFetch('/api/insights');
      const data = await response.json();
      setRecommendations(data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };
  
  // Calculate KPIs
  const activeProjects = projects.filter(p => p.status === 'En cours').length;
  const pendingValidation = projects.filter(p => 
    p.phases?.some(phase => phase.status === 'En attente client')
  ).length;
  const atRiskProjects = projects.filter(p => p.health_score === 'À risque').length;
  const avgProgress = projects.length > 0 
    ? Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
    : 0;
  
  const handleApplyRecommendation = (id) => {
    console.log('Applying recommendation:', id);
    // Navigate to project if it's a project-related insight
    const insight = recommendations.find(r => r.id === id);
    if (insight?.project_id) {
      navigate(`/project/${insight.project_id}`);
    }
    setRecommendations(prev => prev.filter(r => r.id !== id));
  };

  const handleDismissRecommendation = (id) => {
    console.log('Dismissing recommendation:', id);
    setRecommendations(prev => prev.filter(r => r.id !== id));
  };
  
  const getHealthColor = (health) => {
    switch (health) {
      case 'Excellent': return 'text-green-600 bg-green-50 border-green-200';
      case 'Bon': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'À risque': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Critique': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="overview-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="page-title">
            Tableau de Bord
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Vue d'ensemble de vos projets web
          </p>
        </div>
      </div>

      {/* KPI Cards - Clickable */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-section">
        <div onClick={() => navigate('/projects')} className="cursor-pointer">
          <KPICard
            title="Projets actifs"
            value={activeProjects}
            icon={FolderKanban}
            trend="neutral"
            data-testid="kpi-active-projects"
          />
        </div>
        <div onClick={() => navigate('/projects')} className="cursor-pointer">
          <KPICard
            title="En attente validation"
            value={pendingValidation}
            icon={Clock}
            trend="neutral"
            data-testid="kpi-pending-validation"
          />
        </div>
        <div onClick={() => navigate('/projects')} className="cursor-pointer">
          <KPICard
            title="Projets à risque"
            value={atRiskProjects}
            icon={AlertTriangle}
            trend={atRiskProjects > 0 ? 'down' : 'neutral'}
            data-testid="kpi-at-risk"
          />
        </div>
        <div onClick={() => navigate('/reports')} className="cursor-pointer">
          <KPICard
            title="Progression moyenne"
            value={`${avgProgress}%`}
            icon={TrendingUp}
            trend="up"
            data-testid="kpi-avg-progress"
          />
        </div>
      </div>

      {/* AI Insights with AIRecommendation component */}
      <Card className="p-6" data-testid="ai-insights-section">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-primary" />
          <h2 className="text-base font-semibold">Insights Projet</h2>
        </div>
        {insightsLoading ? (
          <div className="text-center py-8 text-muted-foreground">Chargement des insights...</div>
        ) : recommendations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Aucun insight pour le moment</p>
            <p className="text-sm mt-2">Tous vos projets semblent bien se dérouler !</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recommendations.map((recommendation, idx) => (
              <motion.div
                key={recommendation.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <AIRecommendation
                  recommendation={recommendation}
                  onApply={() => handleApplyRecommendation(recommendation.id)}
                  onDismiss={() => handleDismissRecommendation(recommendation.id)}
                />
              </motion.div>
            ))}
          </div>
        )}
      </Card>

      {/* Active Projects */}
      <Card className="p-6" data-testid="active-projects-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Projets en cours</h2>
          <Button variant="outline" size="sm" onClick={() => navigate('/projects')}>
            Voir tout
          </Button>
        </div>
        
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Chargement...</div>
        ) : (
          <div className="space-y-3">
            {projects.filter(p => p.status === 'En cours').slice(0, 5).map((project, idx) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => navigate(`/project/${project.id}`)}
                className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                data-testid={`project-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium truncate">{project.client_name}</h3>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {project.project_type}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Chef de projet: {project.project_manager}
                  </p>
                  <div className="flex items-center gap-2">
                    <Progress value={project.progress} className="h-1.5 flex-1" />
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {project.progress}%
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Badge className={getHealthColor(project.health_score)}>
                    {project.health_score}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {project.estimated_delivery}
                  </span>
                </div>
              </motion.div>
            ))}
            
            {projects.filter(p => p.status === 'En cours').length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                Aucun projet en cours
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default Overview;
